(() => {
  if (window.DppScrollReveal) {
    window.DppScrollReveal.initialize(document);
    return;
  }

  const selector = '[data-dpp-reveal], [data-dpp-reveal-group]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const itemThreshold = 0.45;
  const groupThreshold = 0.24;
  const initialRevealDelay = 167;
  const initialGroupRevealGap = 700;
  const groupRevealGap = 975;
  let initialGroupClaimed = false;
  let groupRevealBlockedUntil = 0;
  let groupRevealTimer = 0;
  const pendingGroupReveals = [];

  const reveal = (element) => {
    element.classList.add('is-revealed');
    if (itemObserver) itemObserver.unobserve(element);
    if (groupObserver) groupObserver.unobserve(element);
  };

  const revealGroup = (element) => {
    reveal(element);
    groupRevealBlockedUntil = performance.now()
      + (element.classList.contains('dpp-reveal-initial') ? initialGroupRevealGap : groupRevealGap);
  };

  const processGroupRevealQueue = () => {
    if (!pendingGroupReveals.length) {
      groupRevealTimer = 0;
      return;
    }

    const wait = Math.max(0, groupRevealBlockedUntil - performance.now());

    if (wait > 0) {
      window.clearTimeout(groupRevealTimer);
      groupRevealTimer = window.setTimeout(processGroupRevealQueue, wait);
      return;
    }

    const element = pendingGroupReveals.shift();
    revealGroup(element);
    processGroupRevealQueue();
  };

  const queueGroupReveal = (element) => {
    if (element.classList.contains('is-revealed') || pendingGroupReveals.includes(element)) return;

    if (groupObserver) groupObserver.unobserve(element);
    pendingGroupReveals.push(element);
    pendingGroupReveals.sort((first, second) => (
      first.compareDocumentPosition(second) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
    ));
    processGroupRevealQueue();
  };

  const itemObserver = 'IntersectionObserver' in window && !reducedMotion.matches
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) reveal(entry.target);
        });
      }, {
        rootMargin: '0px 0px -4% 0px',
        threshold: itemThreshold,
      })
    : null;

  const groupObserver = 'IntersectionObserver' in window && !reducedMotion.matches
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) queueGroupReveal(entry.target);
        });
      }, {
        rootMargin: '0px 0px -10% 0px',
        threshold: groupThreshold,
      })
    : null;

  const isInViewport = (element) => {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
    return visibleHeight > 0;
  };

  const revealAfterInitialPaint = (element) => {
    groupRevealBlockedUntil = Math.max(
      groupRevealBlockedUntil,
      performance.now() + initialRevealDelay + initialGroupRevealGap,
    );

    const startDelay = () => window.setTimeout(() => {
      revealGroup(element);
      processGroupRevealQueue();
    }, initialRevealDelay);

    requestAnimationFrame(() => requestAnimationFrame(startDelay));
  };

  const initializeElement = (element) => {
    if (element.dataset.dppRevealReady === 'true') return;

    element.dataset.dppRevealReady = 'true';

    const hasAnimation = element.hasAttribute('data-dpp-reveal')
      ? element.dataset.dppReveal !== 'none'
      : element.dataset.copyAnimation !== 'none' || element.dataset.truckAnimation !== 'none';

    const observer = element.hasAttribute('data-dpp-reveal-group') ? groupObserver : itemObserver;

    if (!observer || !hasAnimation) {
      element.classList.add('is-revealed');
      return;
    }

    const isGroup = element.hasAttribute('data-dpp-reveal-group');

    if (isGroup && !initialGroupClaimed && isInViewport(element)) {
      initialGroupClaimed = true;
      element.classList.add('dpp-reveal-initial');
      element.classList.add('dpp-reveal-ready');
      revealAfterInitialPaint(element);
      return;
    }

    element.classList.add('dpp-reveal-ready');
    observer.observe(element);
  };

  const initialize = (root = document) => {
    if (root.matches && root.matches(selector)) initializeElement(root);
    if (root.querySelectorAll) root.querySelectorAll(selector).forEach(initializeElement);
  };

  window.DppScrollReveal = { initialize, reveal };

  initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    if (event.target.matches && event.target.matches(selector)) reveal(event.target);
    if (event.target.querySelectorAll) event.target.querySelectorAll(selector).forEach(reveal);
  });
})();
